import type { Context } from '../../evaluator/interface'
import type { Any } from '../../interface'
import type { AstNode, BindingTarget } from '../../parser/types'
import type { NodeTypes } from '../../constants/constants'
import { joinSets } from '../../utils'
import { getAllBindingTargetNames } from '../bindingNode'
import type { BuiltinSpecialExpression, CustomDocs } from '../interface'

export type LoopNode = [typeof NodeTypes.Loop, [[BindingTarget, AstNode][], AstNode], number] // bindings, body

const docs: CustomDocs = {
  category: 'special-expression',
  customVariants: ['loop (bindings) -> body'],
  details: [
    ['bindings', 'binding pairs', 'Comma-separated bindings with initial values, e.g. `n = 10, acc = 0`.'],
    ['body', 'expression', 'The expression to evaluate repeatedly. Use `recur` to loop back with new values.'],
  ],
  description: `Creates a loop with initial bindings. Use \`recur\` inside the body to jump back to the loop head with new binding values.

If \`recur\` is not called, the loop terminates and returns the value of the body expression.`,
  examples: [
    `loop (n = 10, acc = 0) -> do
  if n == 0 then
    acc
  else
    recur(n - 1, acc + n)
  end
end`,
    `loop (n = 5, acc = 1) -> do
  if n <= 1 then
    acc
  else
    recur(n - 1, acc * n)
  end
end`,
  ],
}

export const loopSpecialExpression: BuiltinSpecialExpression<Any, LoopNode> = {
  arity: {},
  docs,
  getUndefinedSymbols: (node, contextStack, { getUndefinedSymbols, builtin }) => {
    const [bindings, body] = node[1]

    const newContext = bindings
      .reduce((context: Context, [target]) => {
        const names = getAllBindingTargetNames(target)

        Object.keys(names).forEach(name => {
          context[name] = { value: true }
        })
        return context
      }, {})

    const bindingValueNodes = bindings.map(([, value]) => value)
    const bindingsResult = getUndefinedSymbols(bindingValueNodes, contextStack, builtin)
    const paramsResult = getUndefinedSymbols([body], contextStack.create(newContext), builtin)
    return joinSets(bindingsResult, paramsResult)
  },
}
